import React, { useContext, useState, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Done as DoneIcon,
  Quiz as QuizIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import Button from '../UIKit/Button';
import { coursesAPI, achievementsAPI, badgesAPI } from '../../services/api';
import { toast } from 'react-toastify';
import NotificationContext from '../../context/NotificationContext';

const LessonActionsSection = ({
  courseId,
  moduleId,
  lessonId,
  completed,
  setCompleted,
  isEnrolled,
  onLessonComplete,
  hasTests,
  onStartTest,
}) => {
  const { t } = useTranslation(['lessons']);
  const { user } = useAuth();
  const notificationContext = useContext(NotificationContext);
  const [isCompleting, setIsCompleting] = useState(false);
  const [hasCompletedOnce, setHasCompletedOnce] = useState(false);

  // Сбрасываем флаг при смене урока
  useEffect(() => {
    setHasCompletedOnce(false);
  }, [lessonId]);

  const handleCompleteLesson = async () => {
    // Предотвращаем повторное нажатие
    if (completed || isCompleting || hasCompletedOnce) {
      return;
    }

    if (!courseId) {
      // Standalone lesson without course
      toast.info(t('lessonDetail.completion.standaloneLesson'));
      return;
    }

    try {
      setIsCompleting(true);
      setHasCompletedOnce(true); // Помечаем что урок уже был завершен в этой сессии
      const response = await coursesAPI.completeLesson(
        courseId,
        moduleId,
        lessonId
      );
      setCompleted(true);
      toast.success(t('lessonDetail.success.lessonCompleted'));

      // Проверяем, завершен ли весь курс
      if (response.data.progress === 100) {
        toast.success(t('lessonDetail.success.courseCompleted'));

        // Проверяем достижения после завершения курса
        try {
          const achievementResponse = await achievementsAPI.check();
          if (
            achievementResponse.data.newAchievements &&
            achievementResponse.data.newAchievements.length > 0
          ) {
            console.log(
              'Получены новые достижения:',
              achievementResponse.data.newAchievements
            );
          }
        } catch (achievementError) {
          console.error('Ошибка при проверке достижений:', achievementError);
        }
      }

      // Проверяем достижения после завершения любого урока
      try {
        const achievementResponse = await achievementsAPI.check();
        if (
          achievementResponse.data.newAchievements &&
          achievementResponse.data.newAchievements.length > 0
        ) {
          console.log(
            'Получены новые достижения:',
            achievementResponse.data.newAchievements
          );
        }
      } catch (achievementError) {
        console.error('Ошибка при проверке достижений:', achievementError);
      }

      // Проверяем значки после завершения любого урока
      try {
        const badgesResponse = await badgesAPI.check();
        if (
          badgesResponse.data.newBadges &&
          badgesResponse.data.newBadges.length > 0
        ) {
          console.log(
            'Получены новые значки:',
            badgesResponse.data.newBadges
          );
          toast.success(
            t('check_badges.new_badges', {
              count: badgesResponse.data.newBadges.length,
            })
          );
        }
      } catch (badgesError) {
        console.error('Ошибка при проверке значков:', badgesError);
      }

      // Принудительно обновляем счетчик уведомлений
      if (notificationContext) {
        console.log('Обновление счетчика уведомлений после завершения урока');
        notificationContext.refreshNotifications();
      }

      // Вызываем callback для обновления данных курса в родительском компоненте
      if (onLessonComplete) {
        await onLessonComplete();
      }
    } catch (err) {
      console.error('Ошибка при завершении урока:', err);
      // Откатываем состояние в случае ошибки
      setCompleted(false);
      setHasCompletedOnce(false); // Сбрасываем флаг в случае ошибки
      toast.error(
        err.response?.data?.message || t('lessonDetail.errors.completeLesson')
      );
    } finally {
      setIsCompleting(false);
    }
  };

  // Don't show if no user
  if (!user) {
    return null;
  }

  return (
    <Box
      sx={{
        mb: 4,
        p: 3,
        borderRadius: '12px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 3,
          position: 'relative',
          flexWrap: 'wrap',
        }}
      >
        {/* Confetti container for celebration */}
        <Box
          id="confetti-container"
          sx={{
            position: 'absolute',
            bottom: '100%',
            right: 0,
            width: '165px',
            height: '128px',
            pointerEvents: 'none',
          }}
        />

        {/* Start Test Button */}
        {hasTests && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<QuizIcon />}
            onClick={onStartTest}
            sx={{
              flex: { xs: '1 1 100%', sm: '0 1 auto' },
              minWidth: { sm: '180px' },
            }}
          >
            {t('lessonDetail.actions.startTest')}
          </Button>
        )}

        {/* Complete Lesson Button */}
        {courseId && (
          <Button
            className="complete-lesson-button"
            variant="contained"
            color={completed ? 'success' : 'primary'}
            onClick={handleCompleteLesson}
            disabled={(!isEnrolled && user?.userMeta?.roles?.includes('admin')) || isCompleting || hasCompletedOnce}
            startIcon={
              isCompleting ? (
                <CircularProgress size={20} color="inherit" />
              ) : completed ? (
                <DoneIcon />
              ) : (
                <CheckCircleIcon />
              )
            }
            sx={{
              flex: { xs: '1 1 100%', sm: '0 1 auto' },
              minWidth: { sm: '180px' },
              ...(completed && {
                pointerEvents: 'none',
                cursor: 'default',
                backgroundColor: (theme) => theme.palette.success.main,
                color: (theme) => theme.palette.success.contrastText,
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.success.main,
                  color: (theme) => theme.palette.success.contrastText,
                },
              }),
            }}
          >
            {isCompleting
              ? t('lessonDetail.completion.completing')
              : completed
              ? t('lessonDetail.completion.completed')
              : t('lessonDetail.completion.complete')}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default LessonActionsSection;
